/* Settings an admin can change, with the defaults Printees publishes on
   printees.co.nz (pages/gang-sheets/src/config.js, 1 Oct 2026). The server
   stores the live values in the `settings` table; the browser receives them
   from GET /api/config. Nothing in the editor hard-codes a size or a price. */

import { z } from 'zod';

export const TRANSFERS = ['dtf', 'uv'] as const;
export type Transfer = (typeof TRANSFERS)[number];

/* ------------------------------------------------------------------ builder */

export const SizePresetSchema = z.object({
  key: z.string().min(1).max(40),
  name: z.string().min(1).max(60),
  widthMm: z.number().positive().max(5000),
  /** optional box height: tall artwork is scaled down to fit inside it */
  maxHeightMm: z.number().positive().max(5000).optional(),
  hint: z.string().max(120).optional()
});
export type SizePreset = z.infer<typeof SizePresetSchema>;

export const BuilderSettingsSchema = z.object({
  /** usable print width of the roll (the by-length sheet width comes from pricing) */
  sheetWidthMm: z.number().positive().max(2000),
  /** keep designs this far from every sheet edge */
  edgeMm: z.number().min(0).max(100),
  /** default minimum space between designs */
  gapMm: z.number().min(0).max(100),
  /** may customers change the gap? (never below minGapMm) */
  allowCustomerGap: z.boolean(),
  minGapMm: z.number().min(0).max(100),
  /** the smallest gap between PRINTED artwork (ink to ink), per transfer type; checked in the
      editor, kept by Auto nest and enforced when ordering */
  inkGapMm: z.object({ dtf: z.number().min(0).max(50), uv: z.number().min(0).max(50) }).default({ dtf: 3, uv: 3 }),
  /** artwork: transparent areas may overlap as long as the printed artwork keeps the gap;
      box: the whole rectangle of every design keeps the gap (the rule before 0.3c). Starts on box:
      Admin switches to artwork once real DTF / UV DTF artwork has been checked with it. */
  spacingCheck: z.enum(['artwork', 'box']).default('box'),
  /** production resolution */
  dpi: z.number().int().min(72).max(1200),
  /** traffic lights */
  dpiGood: z.number().positive(),
  dpiFair: z.number().positive(),
  /** null = low resolution warns but never blocks */
  blockBelowDpi: z.number().positive().nullable(),
  /** alpha at or above this counts as artwork when trimming (1..255) */
  trimAlphaThreshold: z.number().int().min(1).max(255),
  sizePresets: z.array(SizePresetSchema).max(40),
  upload: z.object({
    maxFileMb: z.number().positive().max(5000),
    maxFilesPerProject: z.number().int().positive().max(1000),
    /** lower-case extensions */
    accept: z.array(z.string()).min(1)
  }),
  /** the most transfers one Auto Build will place (keeps the browser snappy) */
  maxItems: z.number().int().positive().max(20000)
});
export type BuilderSettings = z.infer<typeof BuilderSettingsSchema>;

export const DEFAULT_BUILDER_SETTINGS: BuilderSettings = {
  sheetWidthMm: 580,
  edgeMm: 5,
  gapMm: 10,
  allowCustomerGap: true,
  minGapMm: 3,
  inkGapMm: { dtf: 3, uv: 3 },
  spacingCheck: 'box',
  dpi: 300,
  dpiGood: 300,
  dpiFair: 200,
  blockBelowDpi: null,
  trimAlphaThreshold: 1,
  sizePresets: [
    { key: 'nape', name: 'Nape label', widthMm: 70, maxHeightMm: 40, hint: 'Back of the neck' },
    { key: 'small', name: 'Pocket / small logo', widthMm: 75, maxHeightMm: 75 },
    { key: 'chest', name: 'Left chest', widthMm: 90, maxHeightMm: 90, hint: 'About 9 × 9 cm' },
    { key: 'youth', name: 'Youth front', widthMm: 220, maxHeightMm: 280 },
    { key: 'front', name: 'Adult front', widthMm: 280, maxHeightMm: 350 },
    { key: 'large', name: 'Adult large front', widthMm: 300, maxHeightMm: 400 },
    { key: 'back', name: 'Adult back', widthMm: 300, maxHeightMm: 420 },
    { key: 'full', name: 'Oversized / full print', widthMm: 380, maxHeightMm: 480, hint: 'Up to 38 × 48 cm' }
  ],
  upload: {
    maxFileMb: 300,
    maxFilesPerProject: 200,
    accept: ['png', 'jpg', 'jpeg', 'webp', 'svg', 'pdf', 'tif', 'tiff']
  },
  maxItems: 3000
};

/* ------------------------------------------------------------------ pricing */

export const RATE_UNITS = ['m', 'cm', 'mm', 'in', 'ft'] as const;
export type RateUnit = (typeof RATE_UNITS)[number];
export const RATE_UNIT_MM: Record<RateUnit, number> = { m: 1000, cm: 10, mm: 1, in: 25.4, ft: 304.8 };

export const FixedSheetSchema = z.object({
  key: z.string().min(1).max(40),
  name: z.string().min(1).max(60),
  widthMm: z.number().positive(),
  lengthMm: z.number().positive(),
  price: z.number().min(0)
});
export type FixedSheet = z.infer<typeof FixedSheetSchema>;

export const TierSchema = z.object({
  /** total billed metres of by-length film in the order */
  fromMetres: z.number().min(0),
  percentOff: z.number().min(0).max(1),
  /** true: applies only when the total is OVER fromMetres (Printees' "over 1 m" rule) */
  exclusive: z.boolean().optional()
});
export type Tier = z.infer<typeof TierSchema>;

export const ByLengthSchema = z.object({
  enabled: z.boolean(),
  widthMm: z.number().positive(),
  rate: z.number().min(0),
  rateUnit: z.enum(RATE_UNITS),
  minLengthMm: z.number().positive(),
  maxLengthMm: z.number().positive(),
  /** 0 = bill the exact length; otherwise round UP to this step */
  billingStepMm: z.number().min(0),
  /** quick picks offered for a fixed length (1 m, 2 m …) */
  presetsMm: z.array(z.number().positive()).max(20)
});
export type ByLength = z.infer<typeof ByLengthSchema>;

export const TransferPricingSchema = z.object({
  label: z.string(),
  blurb: z.string().optional(),
  fixedSheets: z.array(FixedSheetSchema).max(20),
  byLength: ByLengthSchema,
  tiers: z.array(TierSchema).max(20)
});
export type TransferPricing = z.infer<typeof TransferPricingSchema>;

export const GroupSchema = z.object({
  label: z.string(),
  percentOff: z.number().min(0).max(1).optional(),
  /** their own by-length rate per transfer (same rate unit) */
  rates: z.object({ dtf: z.number().min(0).optional(), uv: z.number().min(0).optional() }).optional(),
  /** false = no volume tiers on top of the group price */
  tiers: z.boolean().optional()
});

export const DiscountCodeSchema = z.object({
  code: z.string().min(2).max(40),
  percentOff: z.number().min(0).max(1).optional(),
  amountOff: z.number().min(0).optional(),
  minSubtotal: z.number().min(0).optional(),
  expiresAt: z.string().optional(),
  active: z.boolean()
});
export type DiscountCode = z.infer<typeof DiscountCodeSchema>;

export const PricingConfigSchema = z.object({
  currency: z.string().min(3).max(3),
  /** prices entered and shown include tax */
  taxInclusive: z.boolean(),
  taxRate: z.number().min(0).max(1),
  taxLabel: z.string(),
  transfers: z.object({ dtf: TransferPricingSchema, uv: TransferPricingSchema }),
  rush: z.object({ enabled: z.boolean(), percent: z.number().min(0).max(5), maxMetres: z.number().min(0), label: z.string() }),
  groups: z.record(z.string(), GroupSchema),
  discountCodes: z.array(DiscountCodeSchema).max(500)
});
export type PricingConfig = z.infer<typeof PricingConfigSchema>;

const TIERS: Tier[] = [
  { fromMetres: 1, percentOff: 0.1, exclusive: true },
  { fromMetres: 5, percentOff: 0.2 },
  { fromMetres: 10, percentOff: 0.3 },
  { fromMetres: 25, percentOff: 0.4 },
  { fromMetres: 50, percentOff: 0.5 }
];

function byLength(rate: number): ByLength {
  return {
    enabled: true,
    widthMm: 580,
    rate,
    rateUnit: 'm',
    minLengthMm: 1000,
    maxLengthMm: 5000,
    billingStepMm: 100,
    presetsMm: [1000, 2000, 3000, 4000, 5000]
  };
}

export const DEFAULT_PRICING: PricingConfig = {
  currency: 'NZD',
  taxInclusive: true,
  taxRate: 0.15,
  taxLabel: 'GST',
  transfers: {
    dtf: {
      label: 'DTF Transfers',
      blurb: 'Clothing and fabric · heat pressed',
      fixedSheets: [
        { key: 'a4', name: 'A4', widthMm: 210, lengthMm: 297, price: 15 },
        { key: 'a3', name: 'A3', widthMm: 297, lengthMm: 420, price: 20 },
        { key: 'half', name: 'Half metre', widthMm: 580, lengthMm: 500, price: 30 }
      ],
      byLength: byLength(55),
      tiers: TIERS
    },
    uv: {
      label: 'UV DTF Stickers',
      blurb: 'Hard surfaces · no heat',
      fixedSheets: [
        { key: 'a4', name: 'A4', widthMm: 210, lengthMm: 297, price: 20 },
        { key: 'a3', name: 'A3', widthMm: 297, lengthMm: 420, price: 30 },
        { key: 'half', name: 'Half metre', widthMm: 580, lengthMm: 500, price: 50 }
      ],
      byLength: byLength(80),
      tiers: TIERS
    }
  },
  rush: { enabled: true, percent: 0.1, maxMetres: 10, label: 'Rush (same day)' },
  groups: {
    wholesale: { label: 'Wholesale', percentOff: 0.15, tiers: true }
  },
  discountCodes: []
};

/** Everything the browser needs to run the builder. */
export interface PublicConfig {
  builder: BuilderSettings;
  pricing: Omit<PricingConfig, 'discountCodes'>;
  links: {
    site: string;
    uploadPage: string;
    guide: string;
    help: string;
    contact: string;
  };
  brand: { name: string; logoOnDark: string };
  /** Save & Email My Gangsheet (0.3e Stage B): on for this browser or not, and the privacy-policy link */
  email?: { mode: 'on' | 'off'; privacyUrl: string | null; sessionDays: number };
}
